import { useMemo, useState } from "react";
import { useTasks, useLists, useUpdateTask } from "../hooks/useTasks";
import { useUiStore } from "../state/uiStore";
import { STATUS, PRIO, type Status } from "../lib/design";
import { isoToDayKey, formatDay, isOverdue } from "../lib/date";
import type { TmTask } from "../api/tm";

const COLUMNS: Status[] = ["todo", "in_progress", "blocked", "done"];

/** Kanban par statut, drag & drop natif HTML5 → PATCH /{id}. */
export function BoardScreen() {
  const tasks = useTasks();
  const lists = useLists();
  const updateTask = useUpdateTask();
  const { selectedTaskId, selectTask, listFilter } = useUiStore();
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<Status | null>(null);

  const byCol = useMemo(() => {
    const cols: Record<Status, TmTask[]> = {
      todo: [],
      in_progress: [],
      blocked: [],
      done: [],
      cancelled: [],
      archived: [],
    };
    for (const t of tasks.data ?? []) {
      if (t.parent_task_id) continue;
      if (listFilter.length && (!t.list_id || !listFilter.includes(t.list_id)))
        continue;
      // cancelled/archived sortent du board (pas de coercition silencieuse en todo).
      if (t.status === "cancelled" || t.status === "archived") continue;
      const s = (t.status in cols ? t.status : "todo") as Status;
      cols[s].push(t);
    }
    return cols;
  }, [tasks.data, listFilter]);

  const drop = (col: Status) => {
    if (dragId) updateTask.mutate({ id: dragId, body: { status: col } });
    setDragId(null);
    setOverCol(null);
  };

  return (
    <>
      <div className="view-header">
        <div className="view-title">Board</div>
        <div className="view-caption">
          Glissez une carte pour changer son statut
        </div>
      </div>
      <div className="board">
        {COLUMNS.map((col) => (
          <div
            key={col}
            className="board-col"
            style={
              overCol === col
                ? { outline: "2px solid var(--accent-border)" }
                : undefined
            }
            onDragOver={(e) => {
              e.preventDefault();
              setOverCol(col);
            }}
            onDragLeave={() => setOverCol(null)}
            onDrop={() => drop(col)}
          >
            <div
              className="group-title"
              style={{
                padding: "2px 4px 8px",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: STATUS[col].dot,
                }}
              />
              {STATUS[col].label}
              <span style={{ fontWeight: 500 }}>{byCol[col].length}</span>
            </div>
            {byCol[col].map((t) => {
              const dueKey = t.due_date ? isoToDayKey(t.due_date) : undefined;
              const listName = lists.data?.find(
                (l) => l.id === t.list_id,
              )?.name;
              return (
                <div
                  key={t.id}
                  className="board-card"
                  draggable
                  onDragStart={() => setDragId(t.id)}
                  onClick={() =>
                    selectTask(t.id === selectedTaskId ? null : t.id)
                  }
                  style={
                    t.id === selectedTaskId
                      ? {
                          borderColor: "var(--accent-border)",
                          background: "var(--accent-wash)",
                        }
                      : undefined
                  }
                >
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      lineHeight: 1.35,
                    }}
                  >
                    {t.title}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      className="chip"
                      style={{
                        color: PRIO[t.priority].color,
                        background: PRIO[t.priority].bg,
                      }}
                    >
                      {PRIO[t.priority].label}
                    </span>
                    {listName && <span className="tag">{listName}</span>}
                    {dueKey && (
                      <span
                        className="task-due"
                        style={
                          col !== "done" && isOverdue(dueKey)
                            ? { color: "var(--danger)", fontWeight: 600 }
                            : undefined
                        }
                      >
                        {formatDay(dueKey)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {byCol[col].length === 0 && (
              <div className="empty-state" style={{ padding: "6px 4px" }}>
                —
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
